import React, { useEffect, useState } from "react";
import { doc, getDoc, setDoc, deleteDoc, serverTimestamp, Timestamp } from "firebase/firestore";
import { Copy, Check, RefreshCw, Trash2, ExternalLink, AlertTriangle } from "lucide-react";
import { useAuth } from "../../hooks/useAuth";
import { db } from "../../firebase/config";
import SectionCard from "../sections/SectionCard";

const TOKEN_TTL_DAYS = 30;
const PORTAL_BASE_URL = "https://forfy.it/commercialista";

function accountantTokenRef(uid) {
  return doc(db, "users", uid, "accountantToken", "current");
}

// Pannello presentazionale riusabile per la gestione del link commercialista.
// Contiene tutta la logica (generazione/rigenerazione/revoca token) ma non ha
// TopBar né wrapper di pagina: va montato dentro AccountantSettingsPage e
// dentro CommercialitaPage.
export default function AccountantAccessPanel({ darkMode }) {
  const { user: authUser } = useAuth();
  const uid = authUser?.uid;

  const [tokenData, setTokenData] = useState(null); // { token, createdAt, expiresAt }
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState(null);
  const [showRevokeConfirm, setShowRevokeConfirm] = useState(false);

  useEffect(() => {
    if (!uid) return;
    let cancelled = false;

    (async () => {
      setLoading(true);
      try {
        const snap = await getDoc(accountantTokenRef(uid));
        if (!cancelled) {
          setTokenData(snap.exists() ? snap.data() : null);
        }
      } catch (err) {
        console.error("Errore lettura token commercialista:", err);
        if (!cancelled) setError("Errore nel caricamento dello stato del token.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [uid]);

  const isExpired = (() => {
    if (!tokenData?.expiresAt) return true;
    const ms = typeof tokenData.expiresAt.toMillis === "function"
      ? tokenData.expiresAt.toMillis()
      : new Date(tokenData.expiresAt).getTime();
    return !ms || ms <= Date.now();
  })();

  const portalUrl = tokenData?.token && uid
    ? `${PORTAL_BASE_URL}/${uid}/${tokenData.token}`
    : null;

  const handleGenerate = async () => {
    if (!uid) return;
    setBusy(true);
    setError(null);
    try {
      const token = crypto.randomUUID();
      const expiresAtDate = new Date(Date.now() + TOKEN_TTL_DAYS * 24 * 60 * 60 * 1000);
      const payload = {
        token,
        createdAt: serverTimestamp(),
        expiresAt: Timestamp.fromDate(expiresAtDate),
      };
      await setDoc(accountantTokenRef(uid), payload);
      setTokenData({ ...payload, createdAt: Timestamp.now(), expiresAt: Timestamp.fromDate(expiresAtDate) });
      setCopied(false);
    } catch (err) {
      console.error("Errore generazione token commercialista:", err);
      setError("Errore durante la generazione del link. Riprova.");
    } finally {
      setBusy(false);
    }
  };

  const handleRevoke = async () => {
    if (!uid) return;
    setBusy(true);
    setError(null);
    try {
      await deleteDoc(accountantTokenRef(uid));
      setTokenData(null);
      setShowRevokeConfirm(false);
      setCopied(false);
    } catch (err) {
      console.error("Errore revoca token commercialista:", err);
      setError("Errore durante la revoca dell'accesso. Riprova.");
    } finally {
      setBusy(false);
    }
  };

  const handleCopy = async () => {
    if (!portalUrl) return;
    try {
      await navigator.clipboard.writeText(portalUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Errore copia link:", err);
    }
  };

  return (
    <div>
      <div>
        <SectionCard
          title="Link condivisibile"
          subtitle={`Il link scade automaticamente dopo ${TOKEN_TTL_DAYS} giorni.`}
          darkMode={darkMode}
        >
          {loading ? (
            <div className={`text-sm ${darkMode ? "text-neutral-500" : "text-neutral-500"}`}>Caricamento...</div>
          ) : !tokenData || isExpired ? (
            <div>
              {tokenData && isExpired && (
                <div className={`mb-4 flex items-start gap-2 p-3 rounded-lg text-xs ${
                  darkMode ? "bg-amber-900/20 text-amber-300" : "bg-amber-50 text-amber-700"
                }`}>
                  <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                  Il link precedente è scaduto. Generane uno nuovo per continuare a condividere l'accesso.
                </div>
              )}
              <p className={`text-sm mb-3 ${darkMode ? "text-neutral-400" : "text-neutral-600"}`}>
                Non hai ancora un link attivo per il commercialista.
              </p>
              <button
                type="button"
                onClick={handleGenerate}
                disabled={busy}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-forfy-500 text-white text-sm hover:bg-forfy-600 disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} />
                Genera link
              </button>
            </div>
          ) : (
            <div>
              <label className={`text-xs ${darkMode ? "text-neutral-500" : "text-neutral-600"}`}>
                Condividi questo link con il tuo commercialista
              </label>
              <div className="mt-1 flex flex-col sm:flex-row gap-2">
                <input
                  type="text"
                  readOnly
                  value={portalUrl}
                  onFocus={(e) => e.target.select()}
                  className={`flex-1 px-3 py-2 border rounded-lg text-sm font-mono ${
                    darkMode
                      ? "border-neutral-700 bg-neutral-800 text-neutral-200"
                      : "border-neutral-200 bg-neutral-50 text-neutral-800"
                  }`}
                />
                <button
                  type="button"
                  onClick={handleCopy}
                  className={`flex items-center justify-center gap-2 px-4 py-2 rounded-lg border text-sm ${
                    darkMode
                      ? "border-neutral-700 bg-neutral-800 text-neutral-300 hover:bg-neutral-700"
                      : "border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-50"
                  }`}
                >
                  {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                  {copied ? "Copiato" : "Copia"}
                </button>
                <a
                  href={portalUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`flex items-center justify-center gap-2 px-4 py-2 rounded-lg border text-sm ${
                    darkMode
                      ? "border-neutral-700 bg-neutral-800 text-neutral-300 hover:bg-neutral-700"
                      : "border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-50"
                  }`}
                >
                  <ExternalLink className="w-4 h-4" />
                  Apri
                </a>
              </div>

              <div className={`mt-4 flex flex-wrap gap-2`}>
                <button
                  type="button"
                  onClick={handleGenerate}
                  disabled={busy}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs ${
                    darkMode
                      ? "border-neutral-700 bg-neutral-800 text-neutral-300 hover:bg-neutral-700"
                      : "border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-50"
                  } disabled:opacity-50`}
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} />
                  Rigenera token
                </button>

                {!showRevokeConfirm ? (
                  <button
                    type="button"
                    onClick={() => setShowRevokeConfirm(true)}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs ${
                      darkMode ? "bg-red-900/20 text-red-400 hover:bg-red-900/30" : "bg-red-50 text-red-600 hover:bg-red-100"
                    }`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    Revoca accesso
                  </button>
                ) : (
                  <div className="flex items-center gap-2">
                    <span className={`text-xs ${darkMode ? "text-neutral-400" : "text-neutral-600"}`}>Confermi?</span>
                    <button
                      type="button"
                      onClick={handleRevoke}
                      disabled={busy}
                      className="px-3 py-1.5 rounded-lg bg-red-600 text-white text-xs hover:bg-red-700 disabled:opacity-50"
                    >
                      Sì, revoca
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowRevokeConfirm(false)}
                      className={`px-3 py-1.5 rounded-lg text-xs ${
                        darkMode ? "bg-neutral-700 text-neutral-300 hover:bg-neutral-600" : "bg-neutral-200 text-neutral-700 hover:bg-neutral-300"
                      }`}
                    >
                      Annulla
                    </button>
                  </div>
                )}
              </div>

              <p className={`mt-4 text-xs ${darkMode ? "text-neutral-500" : "text-neutral-500"}`}>
                Rigenerando il token, il link precedente smette immediatamente di funzionare.
              </p>
            </div>
          )}

          {error && <div className="mt-3 text-xs text-red-500">{error}</div>}
        </SectionCard>
      </div>

      <div className="mt-6">
        <SectionCard title="Cosa può fare il commercialista" darkMode={darkMode}>
          <ul className={`text-sm space-y-2 list-disc pl-5 ${darkMode ? "text-neutral-400" : "text-neutral-600"}`}>
            <li>Consultare l'elenco delle fatture (emesse, pagate, da incassare) e i totali dell'anno.</li>
            <li>Scaricare l'elenco fatture in CSV.</li>
            <li>Caricare F24 e dichiarazioni direttamente sul tuo profilo.</li>
            <li>Non ha accesso alla tua email, password o altri dati sensibili dell'account.</li>
          </ul>
        </SectionCard>
      </div>
    </div>
  );
}
